import { Save, X } from 'lucide-react';
// Bundled worker (copied to /assets by Vite)
// eslint-disable-next-line import/no-webpack-loader-syntax
import workerSrc from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Rnd } from 'react-rnd';
import { toast } from 'sonner';

import { Button } from '@/components/ui/catalyst/button';
import { useGetDemandExampleQuery } from '@/services/api/demandExampleService';
import {
  DemandExampleTemplate,
  DemandExampleTemplateSection,
  useCreateSectionMutation,
  useCreateTemplateMutation,
  useDeleteSectionMutation,
  useRegenerateImagesMutation,
  useUpdateTemplateMutation,
} from '@/services/api/demandExampleTemplateService';
import { useListStaticProofreadingRulesQuery } from '@/services/api/proofreadingService';

pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;

interface Box {
  id: string;
  x: number; // 0–1 relative to the full viewer width
  y: number; // 0–1 relative to the full viewer height (all pages stacked)
  width: number; // 0–1 relative
  height: number; // 0–1 relative
  section_name: string;
  description?: string;
  assigned_rule_ids?: string[];
}

interface TemplateBuilderProps {
  demandExampleId: string;
  template: DemandExampleTemplate | null;
  existingSections?: DemandExampleTemplateSection[];
  initialBoxes?: Box[];
  onClose: () => void;
}

const COLOR_PALETTE = [
  '#ef4444', // red-500
  '#f97316', // orange-500
  '#eab308', // amber-500
  '#10b981', // emerald-500
  '#14b8a6', // teal-500
  '#3b82f6', // blue-500
  '#6366f1', // indigo-500
  '#a855f7', // violet-500
  '#d946ef', // fuchsia-500
  '#ec4899', // pink-500
];

const getColor = (index: number) => COLOR_PALETTE[index % COLOR_PALETTE.length];

export const TemplateBuilder = ({
  demandExampleId,
  template,
  existingSections,
  initialBoxes,
  onClose,
}: TemplateBuilderProps) => {
  const { data: demandExampleData } = useGetDemandExampleQuery(demandExampleId);
  const pdfUrl: string | null = demandExampleData?.demand ?? null;
  const firmId = demandExampleData?.firm_details?.id || '';
  const { data: staticRules } = useListStaticProofreadingRulesQuery(
    { firmId },
    { skip: !firmId },
  );
  const [pageCount, setPageCount] = useState(0);
  const [boxes, setBoxes] = useState<Box[]>(initialBoxes ?? []);
  const [drawing, setDrawing] = useState<null | {
    startX: number;
    startY: number;
  }>(null);
  const [currentRect, setCurrentRect] = useState<null | {
    x: number;
    y: number;
    w: number;
    h: number;
  }>(null);
  const [pageLayouts, setPageLayouts] = useState<
    Record<
      number,
      {
        w: number;
        h: number;
        top: number;
        left: number;
        canvas?: HTMLCanvasElement | null;
        canvasOffsetX?: number;
        canvasWidth?: number;
      }
    >
  >({});
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [templateName, setTemplateName] = useState(template?.name ?? '');
  const [headerId, setHeaderId] = useState<string | undefined>(template?.id);
  const [saving, setSaving] = useState(false);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const pagesReady =
    pageCount > 0 && Object.keys(pageLayouts || {}).length === pageCount;

  // mutations
  const [createTemplate] = useCreateTemplateMutation();
  const [createSection] = useCreateSectionMutation();
  const [updateTemplate] = useUpdateTemplateMutation();
  const [deleteSection] = useDeleteSectionMutation();
  const [regenerateImages] = useRegenerateImagesMutation();

  // Delete key removes selected box
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      // If the user is typing in an input/textarea/content-editable element, ignore
      const t = e.target as HTMLElement | null;
      if (
        t &&
        (t.tagName === 'INPUT' ||
          t.tagName === 'TEXTAREA' ||
          t.isContentEditable)
      ) {
        return;
      }

      if (!selectedId) return;
      if (e.key === 'Delete' || e.key === 'Backspace') {
        setBoxes((prev) => prev.filter((b) => b.id !== selectedId));
        setSelectedId(null);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [selectedId]);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!canvasRef.current) return;

    // Ignore clicks that originate inside an existing Rnd box
    if ((e.target as HTMLElement).closest('[data-template-box]')) {
      return;
    }

    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top + canvasRef.current.scrollTop;
    // store start positions relative to the entire scrollable area, not just viewport
    setDrawing({ startX: x, startY: y });
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!drawing || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const endX = e.clientX - rect.left;
    const endY = e.clientY - rect.top + canvasRef.current.scrollTop;

    const x = Math.min(drawing.startX, endX);
    const y = Math.min(drawing.startY, endY);
    const width = Math.abs(drawing.startX - endX);
    const height = Math.abs(drawing.startY - endY);

    setCurrentRect({
      x: x / rect.width,
      y: y / canvasRef.current.scrollHeight,
      w: width / rect.width,
      h: height / canvasRef.current.scrollHeight,
    });
  };

  const handleMouseUp = () => {
    if (!drawing || !canvasRef.current || !currentRect) {
      setDrawing(null);
      return;
    }

    const { x, y, w, h } = currentRect;
    if (
      w * canvasRef.current.clientWidth < 5 ||
      h * canvasRef.current.scrollHeight < 5
    ) {
      setDrawing(null);
      setCurrentRect(null);
      return;
    }

    const newBox: Box = {
      id: `${Date.now()}`,
      x,
      y,
      width: w,
      height: h,
      section_name: '',
      description: '',
      assigned_rule_ids: [],
    };
    setBoxes((prev) => [...prev, newBox]);
    setDrawing(null);
    setCurrentRect(null);
  };

  useEffect(() => {
    if (!template || !existingSections?.length) return;
    if (!canvasRef.current) return;
    if (!pagesReady) return; // canvases not ready yet

    const containerW = canvasRef.current.clientWidth || 1;
    const containerH = (() => {
      const vals = Object.values(pageLayouts || {});
      if (vals.length) {
        return (
          Math.max(...vals.map((p) => (p ? (p.top || 0) + (p.h || 0) : 0))) || 1
        );
      }
      return canvasRef.current?.scrollHeight || 1;
    })();
    const loaded: Box[] = [];

    existingSections.forEach((sec) => {
      const bbDict = sec.bounding_boxes || {};

      let minGX = Number.POSITIVE_INFINITY;
      let minGY = Number.POSITIVE_INFINITY;
      let maxGX = 0;
      let maxGY = 0;

      Object.entries(bbDict).forEach(([pageStr, bb]) => {
        const page = pageLayouts[Number(pageStr)];
        if (!page) return;

        const viewerW = page.canvasWidth ?? page.w; // actual canvas width
        const viewerH = page.h; // height remains div height ~ canvas height

        const bbox = bb as { x1: number; y1: number; x2: number; y2: number };

        const relX1 = bbox.x1;
        const relX2 = bbox.x2;
        const relY1 = bbox.y1;
        const relY2 = bbox.y2;

        const globalX1 =
          (page.left + (page.canvasOffsetX ?? 0) + relX1 * viewerW) /
          containerW;
        const globalY1 = (page.top + relY1 * viewerH) / containerH;
        const globalX2 =
          (page.left + (page.canvasOffsetX ?? 0) + relX2 * viewerW) /
          containerW;
        const globalY2 = (page.top + relY2 * viewerH) / containerH;

        if (globalX1 < minGX) minGX = globalX1;
        if (globalY1 < minGY) minGY = globalY1;
        if (globalX2 > maxGX) maxGX = globalX2;
        if (globalY2 > maxGY) maxGY = globalY2;
      });

      if (minGX === Number.POSITIVE_INFINITY) return; // no pages processed

      loaded.push({
        id: `${sec.id}`,
        x: minGX,
        y: minGY,
        width: maxGX - minGX,
        height: maxGY - minGY,
        section_name: sec.section_name,
        description: sec.description ?? '',
        assigned_rule_ids: sec.assigned_rule_ids ?? [],
      });
    });

    setBoxes(loaded);
  }, [template, existingSections, pageLayouts, pagesReady]);

  const handleSaveTemplate = async () => {
    setSaving(true);
    try {
      // ------------------------------------------------------------------
      // 1. Ensure we have a valid template header and capture its ID
      // ------------------------------------------------------------------
      let tplId = headerId;

      if (!tplId) {
        const tpl = await createTemplate({
          demandExampleId,
          payload: {
            name: templateName || `Template ${new Date().toLocaleString()}`,
            demand_example: demandExampleId,
          },
        }).unwrap();
        tplId = tpl.id;
        // Update state asynchronously (don't rely on this inside the function)
        setHeaderId(tpl.id);
      } else if (template && templateName && templateName !== template.name) {
        if (tplId) {
          await updateTemplate({
            demandExampleId,
            templateId: tplId,
            payload: { name: templateName },
          });
        }
      }

      if (!tplId) {
        throw new Error('Failed to determine template ID');
      }

      const templateIdEnsured = tplId as string;

      if (template && existingSections?.length) {
        await Promise.all(
          existingSections.map((sec) =>
            deleteSection({
              demandExampleId,
              templateId: templateIdEnsured,
              sectionId: sec.id,
            }),
          ),
        );
      }

      if (!canvasRef.current) return;

      const containerW = canvasRef.current.clientWidth || 1;
      const containerH = (() => {
        const vals = Object.values(pageLayouts || {});
        if (vals.length) {
          return (
            Math.max(...vals.map((p) => (p ? (p.top || 0) + (p.h || 0) : 0))) ||
            1
          );
        }
        return canvasRef.current?.scrollHeight || 1;
      })();

      await Promise.all(
        boxes.map(async (box, idx) => {
          const boxTop = box.y * containerH;
          const boxBottom = boxTop + box.height * containerH;

          const bounding_boxes: Record<
            string,
            { x1: number; y1: number; x2: number; y2: number }
          > = {};

          Object.values(pageLayouts).forEach((page) => {
            if (!page.canvas) return;

            const pageTop = page.top;
            const pageBottom = page.top + page.h;
            const overlapTop = Math.max(boxTop, pageTop);
            const overlapBottom = Math.min(boxBottom, pageBottom);
            const overlapHeight = overlapBottom - overlapTop;

            if (overlapHeight <= 0) return;

            // Use the rendered CSS width, not the high-DPI canvas pixel width,
            // otherwise x-coordinates shrink on retina displays and the crop is cut off.
            const pageW = page.canvasWidth ?? page.w;

            const pageStartX = page.left; // canvas left edge (already correct)
            const pageEndX = pageStartX + pageW;

            // Global X range of the box in px
            const globalStartX = box.x * containerW;
            const globalEndX = (box.x + box.width) * containerW;

            // Horizontal intersection with the page
            const intersectStartX = Math.max(globalStartX, pageStartX);
            const intersectEndX = Math.min(globalEndX, pageEndX);
            if (intersectEndX <= intersectStartX) return; // nothing on this page

            // amount of extra padding in page-relative units (5 px here)
            const PAD = 5; // pixels

            const localStartX = Math.max(0, intersectStartX - pageStartX - PAD);
            const localEndX = Math.min(pageW, intersectEndX - pageStartX + PAD);

            const normX1 = localStartX / pageW;
            const normX2 = localEndX / pageW;

            // vertical within page
            const localY = overlapTop - pageTop;
            let sliceH = overlapHeight;
            if (localY + sliceH > page.h) {
              sliceH = page.h - localY;
            }
            const normY1 = localY / page.h;
            const normY2 = (localY + sliceH) / page.h;

            const pageKey = Object.keys(pageLayouts).find(
              (k) => pageLayouts[Number(k)] === page,
            ) as string | undefined;
            if (!pageKey) return;

            // create normalised box per intersecting page
            const pageRelX1 = normX1;
            const pageRelX2 = normX2;
            const pageRelY1 = normY1;
            const pageRelY2 = normY2;

            bounding_boxes[pageKey] = {
              x1: pageRelX1,
              y1: pageRelY1,
              x2: pageRelX2,
              y2: pageRelY2,
            };
          });

          if (!Object.keys(bounding_boxes).length) return;

          // create tiny transparent PNG placeholder
          const placeholderCanvas = document.createElement('canvas');
          placeholderCanvas.width = 1;
          placeholderCanvas.height = 1;
          const placeholderBlob: Blob = await new Promise((resolve) => {
            placeholderCanvas.toBlob((b) => resolve(b as Blob), 'image/png');
          });

          const formData = new FormData();
          formData.append('template', templateIdEnsured);
          formData.append('section_name', box.section_name);
          if (box.description !== undefined) {
            formData.append('description', box.description ?? '');
          }
          formData.append('order', String(idx));
          formData.append('bounding_boxes', JSON.stringify(bounding_boxes));
          formData.append('image', placeholderBlob, `${box.section_name}.png`);
          if (box.assigned_rule_ids && box.assigned_rule_ids.length > 0) {
            formData.append(
              'assigned_rule_ids',
              JSON.stringify(box.assigned_rule_ids),
            );
          }

          await createSection({
            demandExampleId,
            templateId: templateIdEnsured,
            formData,
          }).unwrap();
        }),
      );

      // Regenerate thumbnails on backend via RTK mutation
      await regenerateImages({
        demandExampleId,
        templateId: templateIdEnsured,
      });

      toast.success('Template saved');
      onClose();
    } catch (e: unknown) {
      if (
        e &&
        typeof e === 'object' &&
        'data' in e &&
        e.data &&
        typeof e.data === 'object' &&
        'non_field_errors' in e.data
      ) {
        const errors = e.data.non_field_errors;
        if (Array.isArray(errors)) {
          toast.error(errors.join(', '));
        } else {
          toast.error('Failed to save template');
        }
      } else {
        toast.error('Failed to save template');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDragStart = (id: string) => setDraggingId(id);
  const handleDragOver = (id: string) => {
    if (!draggingId || draggingId === id) return;
    setBoxes((prev) => {
      const draggingIdx = prev.findIndex((b) => b.id === draggingId);
      const targetIdx = prev.findIndex((b) => b.id === id);
      if (draggingIdx === -1 || targetIdx === -1) return prev;
      const newArr = [...prev];
      const [moved] = newArr.splice(draggingIdx, 1);
      newArr.splice(targetIdx, 0, moved);
      return newArr;
    });
  };
  const handleDragEnd = () => setDraggingId(null);

  return (
    <div className="w-full max-h-screen h-full flex flex-col flex-1 min-h-0 overflow-hidden gap-2">
      <div className="flex items-center gap-2 px-2">
        <input
          type="text"
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
          placeholder="Template name"
          className="border px-2 py-1 rounded text-sm flex-1"
        />
      </div>
      {pdfUrl && (
        <div className="flex gap-4 flex-1 min-h-0 overflow-hidden">
          {/* PDF & overlay */}
          <div
            className="flex-1 overflow-auto relative min-h-0"
            ref={canvasRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            role="presentation"
            tabIndex={-1}
            aria-label="PDF viewport"
          >
            <Document
              file={pdfUrl}
              onLoadSuccess={(info: { numPages: number }) =>
                setPageCount(info.numPages)
              }
              onLoadError={(e) => setLoadError(String(e))}
              className="space-y-4"
            >
              {Array.from({ length: pageCount }, (_, idx) => (
                <div
                  key={idx}
                  className="relative"
                  ref={(el) => {
                    if (!el) return;
                    const rect = el.getBoundingClientRect();
                    const top = el.offsetTop;
                    const left = el.offsetLeft;

                    setPageLayouts((prev) => {
                      const existing = prev[idx + 1];
                      const cv = el.querySelector('canvas');
                      const cvRect = cv?.getBoundingClientRect();
                      const next = {
                        w: rect.width,
                        h: rect.height,
                        top,
                        left,
                        canvas: cv,
                        canvasOffsetX: cvRect ? cvRect.left - rect.left : 0,
                        canvasWidth: cvRect ? cvRect.width : rect.width,
                      } as (typeof prev)[1];

                      if (
                        existing &&
                        existing.w === next.w &&
                        existing.h === next.h &&
                        existing.top === next.top &&
                        existing.left === next.left &&
                        existing.canvasWidth === next.canvasWidth &&
                        existing.canvasOffsetX === next.canvasOffsetX
                      ) {
                        return prev; // no change
                      }
                      return { ...prev, [idx + 1]: next };
                    });
                  }}
                >
                  <Page
                    pageNumber={idx + 1}
                    scale={1.2}
                    renderAnnotationLayer={false}
                    renderTextLayer={false}
                  />
                </div>
              ))}
            </Document>

            {/* Global overlay for boxes & drawing (spans full scroll height) */}
            <div
              className="absolute left-0 right-0 top-0 pointer-events-none"
              style={{
                height: pagesReady
                  ? Math.max(
                      ...Object.values(pageLayouts).map((p) =>
                        p ? (p.top || 0) + (p.h || 0) : 0,
                      ),
                    )
                  : (canvasRef.current?.scrollHeight ?? '100%'),
              }}
            >
              {boxes.map((b: Box, idx) => {
                if (!canvasRef.current) return null;
                const containerW = canvasRef.current.clientWidth;
                const containerH = pagesReady
                  ? Math.max(
                      ...Object.values(pageLayouts).map((p) =>
                        p ? (p.top || 0) + (p.h || 0) : 0,
                      ),
                    )
                  : canvasRef.current.scrollHeight;
                const color = getColor(idx);
                return (
                  <Rnd
                    data-template-box
                    key={b.id}
                    size={{
                      width: b.width * containerW,
                      height: b.height * containerH,
                    }}
                    position={{ x: b.x * containerW, y: b.y * containerH }}
                    bounds="parent"
                    enableResizing
                    onDragStop={(e, d) => {
                      const newX = d.x / containerW;
                      const newY = d.y / containerH;
                      setBoxes((prev) =>
                        prev.map((x) =>
                          x.id === b.id ? { ...x, x: newX, y: newY } : x,
                        ),
                      );
                    }}
                    onResizeStop={(_e, _dir, ref, _delta, pos) => {
                      const newW = ref.offsetWidth / containerW;
                      const newH = ref.offsetHeight / containerH;
                      const newX = pos.x / containerW;
                      const newY = pos.y / containerH;
                      setBoxes((prev) =>
                        prev.map((x) =>
                          x.id === b.id
                            ? {
                                ...x,
                                x: newX,
                                y: newY,
                                width: newW,
                                height: newH,
                              }
                            : x,
                        ),
                      );
                    }}
                    style={{
                      border:
                        selectedId === b.id
                          ? `2px solid ${color}`
                          : `2px dashed ${color}80`,
                      background: `${color}1A`, // ~10% opacity
                      pointerEvents: 'auto',
                    }}
                    onClick={() => setSelectedId(b.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                      if (e.key === 'Enter' || e.key === ' ')
                        setSelectedId(b.id);
                    }}
                    className="group"
                  >
                    {b.section_name && (
                      <div className="absolute -top-5 left-0 bg-blue-600 text-white text-xs px-2 py-0.5 rounded opacity-90 group-hover:opacity-100 shadow-sm">
                        {b.section_name}
                      </div>
                    )}
                  </Rnd>
                );
              })}

              {currentRect && (
                <div
                  className="absolute border-2 border-blue-400 border-dashed bg-blue-400/10"
                  style={{
                    left: `${currentRect.x * 100}%`,
                    top: `${currentRect.y * 100}%`,
                    width: `${currentRect.w * 100}%`,
                    height: `${currentRect.h * 100}%`,
                  }}
                />
              )}
            </div>
          </div>
          {/* side panel */}
          <div className="w-96 border-l border-zinc-200 px-2 overflow-y-auto min-h-0 h-full">
            <h4 className="font-medium text-sm mt-2 mb-1">
              Sections ({boxes.length})
            </h4>
            {boxes.map((b, idx) => {
              const color = getColor(idx);
              return (
                <div
                  key={b.id}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                    if (e.key === 'Enter' || e.key === ' ') setSelectedId(b.id);
                  }}
                  className={`border rounded p-2 mb-2 text-xs flex flex-col gap-1 cursor-pointer ${selectedId === b.id ? 'ring-2 ring-blue-500 bg-blue-50' : ''}`}
                  style={{ borderLeft: `4px solid ${color}` }}
                  onClick={() => setSelectedId(b.id)}
                  draggable
                  onDragStart={() => handleDragStart(b.id)}
                  onDragOver={(e) => {
                    e.preventDefault();
                    handleDragOver(b.id);
                  }}
                  onDragEnd={handleDragEnd}
                >
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] font-bold" style={{ color }}>
                      {idx + 1}
                    </span>
                    <input
                      type="text"
                      placeholder="Section name"
                      className="border px-1 py-0.5 text-xs rounded w-full"
                      value={b.section_name}
                      onChange={(e) =>
                        setBoxes((prev) =>
                          prev.map((x) =>
                            x.id === b.id
                              ? { ...x, section_name: e.target.value }
                              : x,
                          ),
                        )
                      }
                    />
                  </div>
                  <input
                    type="text"
                    placeholder="Description (optional)"
                    className="border px-1 py-0.5 text-xs rounded w-full mt-1"
                    value={b.description ?? ''}
                    onChange={(e) =>
                      setBoxes((prev) =>
                        prev.map((x) =>
                          x.id === b.id
                            ? { ...x, description: e.target.value }
                            : x,
                        ),
                      )
                    }
                  />
                  {staticRules && staticRules.length > 0 && (
                    <div className="mt-1">
                      <div
                        id={`rule-label-${b.id}`}
                        className="block text-[10px] text-zinc-600 mb-1"
                      >
                        Assigned Proofreading Rules
                      </div>
                      <div className="flex flex-wrap gap-1 mb-1">
                        {(b.assigned_rule_ids || []).map((rid) => {
                          const rule = staticRules.find((r) => r.id === rid);
                          return (
                            <span
                              key={rid}
                              className="px-1.5 py-0.5 text-[10px] rounded bg-blue-50 text-blue-700 border border-blue-200 inline-flex items-center gap-1"
                            >
                              {rule ? `${rid}:${rule.title}` : rid}
                              <button
                                type="button"
                                aria-label={`Remove ${rule?.title || rid}`}
                                className="text-blue-700/70 hover:text-blue-900"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setBoxes((prev) =>
                                    prev.map((x) =>
                                      x.id === b.id
                                        ? {
                                            ...x,
                                            assigned_rule_ids: (
                                              x.assigned_rule_ids || []
                                            ).filter((id) => id !== rid),
                                          }
                                        : x,
                                    ),
                                  );
                                }}
                              >
                                ×
                              </button>
                            </span>
                          );
                        })}
                      </div>
                      <select
                        id={`rule-select-${b.id}`}
                        aria-labelledby={`rule-label-${b.id}`}
                        className="border px-1 py-1 text-xs rounded w-full"
                        onChange={(e) => {
                          const rid = e.target.value;
                          if (!rid) return;
                          setBoxes((prev) =>
                            prev.map((x) =>
                              x.id === b.id
                                ? {
                                    ...x,
                                    assigned_rule_ids: Array.from(
                                      new Set([
                                        ...(x.assigned_rule_ids || []),
                                        rid,
                                      ]),
                                    ),
                                  }
                                : x,
                            ),
                          );
                        }}
                      >
                        <option value="">+ Add rule…</option>
                        {staticRules.map((r) => (
                          <option
                            key={r.id}
                            value={r.id}
                            disabled={(b.assigned_rule_ids || []).includes(
                              r.id,
                            )}
                          >
                            {`${r.id}:${r.title}`}
                          </option>
                        ))}
                      </select>
                      {(b.assigned_rule_ids?.length || 0) > 0 && (
                        <button
                          type="button"
                          className="mt-1 text-[10px] text-red-600"
                          onClick={() =>
                            setBoxes((prev) =>
                              prev.map((x) =>
                                x.id === b.id
                                  ? { ...x, assigned_rule_ids: [] }
                                  : x,
                              ),
                            )
                          }
                        >
                          Clear assigned rules
                        </button>
                      )}
                    </div>
                  )}
                  <div className="flex gap-2 text-zinc-500">
                    <button
                      className="ml-auto text-red-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        setBoxes((prev) => prev.filter((x) => x.id !== b.id));
                        if (selectedId === b.id) setSelectedId(null);
                      }}
                      type="button"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {!pdfUrl && loadError && (
        <p className="text-red-500 text-sm">Failed to load PDF: {loadError}</p>
      )}
      {!pdfUrl && !loadError && <p>Loading PDF…</p>}
      <div className="flex gap-2 justify-end mt-2">
        <Button outline onClick={onClose} aria-label="cancel">
          <X className="h-4 w-4" />
        </Button>
        <Button
          onClick={handleSaveTemplate}
          disabled={!boxes.length || boxes.some((b) => !b.section_name)}
          aria-label="save template"
        >
          <Save className="h-4 w-4 mr-1" /> Save
        </Button>
      </div>

      {saving && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-md p-6 flex flex-col items-center gap-2">
            <svg
              className="animate-spin h-6 w-6 text-blue-600"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              />
            </svg>
            <p className="text-sm mt-2">Saving template…</p>
          </div>
        </div>
      )}
    </div>
  );
};
